import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { LiveReconnectHintComponent } from '../../../components/live-reconnect-hint/live-reconnect-hint.component';
import { LivePipeCardComponent } from './live-pipe-card.component';
import { LivePipelineService } from '../../../services/live-pipeline.service';
import { LivePipeline, LivePipelineEntry, livePipelineKey } from '../../../models/live-pipeline.model';
import { ProjectService } from '../../../services/project.service';
import { RepositoryBindingService } from '../../../services/repository-binding.service';

/**
 * Die Dashboard-Sektion „Pipelines · Live" (NEX-1232): je laufendem Durchlauf aus einem
 * angebundenen Repository eine Karte.
 *
 * <p>Gefragt wird je Projekt, weil der Endpunkt je Projekt antwortet; die Liste der
 * Repository-Bindungen entscheidet vorher, welche Projekte ueberhaupt gefragt
 * werden. Ohne eine einzige Bindung bleibt die Sektion fort — sie waere sonst eine
 * dauerhaft leere Kachel fuer eine Integration, die niemand eingerichtet hat.
 *
 * <p>Nachgeladen wird nur nach einem Reconnect: neue Staende und neu gestartete
 * Durchlaeufe kommen ueber das Projekt-Topic herein, und der Eintrag darin ist
 * derselbe, den auch der Endpunkt liefert. Was das Topic waehrend der Luecke
 * gemeldet haette, ist aber verloren — ohne einmaligen Abgleich per Endpunkt bliebe
 * ein in der Luecke beendeter Durchlauf auf „Laeuft" stehen. Abgeglichen wird nur
 * die Live-Liste der schon beobachteten Projekte; welche Projekte angebunden sind,
 * aendert ein Verbindungsabriss nicht.
 *
 * <p>Selbst ausgeloeste und beobachtete Laeufe stehen in einer Liste (NEX-1292): fuer
 * die Sektion sind es dieselben Eintraege aus demselben Endpunkt und demselben Topic,
 * und die Sortierung „neueste zuerst" ordnet sie ohne Ansehen der Herkunft.
 *
 * <p>Gezeigt werden allein die laufenden — dieselbe Regel wie im {@code LiveRunsStore}.
 * Der Endpunkt liefert auch kurz zuvor beendete; die faellt die Sektion beim Laden
 * heraus, und das Topic meldet jeden Durchlauf ein letztes Mal mit seinem Ende, was
 * seine Karte entfernt. Ohne dieses Loeschen waechst die Liste einer offenen Seite den
 * ganzen Tag, seit jeder Lauf des angebundenen Repositories hier einlaeuft — und sie
 * zeigte dann etwas anderes als dieselbe Seite nach einem Neuladen.
 */
@Component({
  selector: 'app-live-pipeline-section',
  imports: [
    RouterLink,
    MatIcon,
    TranslatePipe,
    SectionTitleComponent,
    LiveReconnectHintComponent,
    LivePipeCardComponent,
  ],
  providers: [LivePipelineService],
  templateUrl: './live-pipeline-section.component.html',
  styleUrl: './live-pipeline-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LivePipelineSectionComponent {
  private readonly projects = inject(ProjectService);
  private readonly repositories = inject(RepositoryBindingService);
  private readonly feed = inject(LivePipelineService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly headingId = 'live-pipeline-heading';

  private readonly watchedProjectIds = signal<string[]>([]);

  protected readonly pipelines = signal<LivePipeline[]>([]);

  protected readonly hasRepository = computed(() => this.watchedProjectIds().length > 0);

  /**
   * Die Karten bleiben stehen, wenn die Verbindung faellt: sie zeigen den letzten
   * bekannten Stand, und der Hinweis sagt, dass er nicht mehr fortgeschrieben wird
   * (NEX-1307). Das gilt auch fuer die leere Zeile — „nichts laeuft" ist ohne
   * Verbindung ebenso nur der letzte Stand.
   */
  protected readonly connectionLost = computed(() => this.feed.connection() === 'lost');

  protected readonly key = livePipelineKey;

  /**
   * Der Absprung in die Testlauf-Uebersicht. Die Uebersicht haengt am Projekt, die
   * Sektion nicht — gezeigt wird deshalb das Projekt der obersten Karte, ersatzweise
   * das erste mit angebundenem Repository.
   */
  protected readonly allRunsLink = computed(() => {
    const projectId = this.pipelines()[0]?.projectId ?? this.watchedProjectIds()[0];
    return projectId ? ['/projects', projectId, 'test-runs'] : null;
  });

  constructor() {
    this.projects
      .listForCurrentUser()
      .pipe(
        catchError(() => of([])),
        switchMap((projects) => this.projectIdsWithRepository(projects.map((project) => project.id))),
        switchMap((projectIds) => this.livePipelines(projectIds).pipe(map((pipelines) => ({ projectIds, pipelines })))),
        takeUntilDestroyed(),
      )
      .subscribe(({ projectIds, pipelines }) => {
        this.watchedProjectIds.set(projectIds);
        this.pipelines.set(newestFirst(pipelines.filter(isRunning)));
        this.feed.connect(projectIds);
      });

    this.feed.events
      .pipe(takeUntilDestroyed())
      .subscribe(({ projectId, entry }) => this.applyFeedEntry(projectId, entry));

    this.feed.reconnected
      .pipe(
        switchMap(() => this.reloadedLivePipelines(this.watchedProjectIds())),
        takeUntilDestroyed(),
      )
      .subscribe((reloaded) => this.replaceReloaded(reloaded));

    this.destroyRef.onDestroy(() => this.feed.disconnect());
  }

  /**
   * Ein Projekt, dessen Bindungen sich nicht laden lassen, gilt als unangebunden:
   * die Sektion kann fuer dieses Projekt ohnehin nichts zeigen, und ein Fehler darf
   * die uebrigen Projekte nicht mitnehmen.
   */
  private projectIdsWithRepository(projectIds: string[]): Observable<string[]> {
    return forkJoinOrEmpty(
      projectIds.map((projectId) =>
        this.repositories.list(projectId).pipe(
          map((bindings) => (bindings.length > 0 ? projectId : null)),
          catchError(() => of(null)),
        ),
      ),
    ).pipe(map((ids) => ids.filter((id): id is string => id !== null)));
  }

  private livePipelines(projectIds: string[]): Observable<LivePipeline[]> {
    return forkJoinOrEmpty(
      projectIds.map((projectId) =>
        this.projectLivePipelines(projectId).pipe(catchError(() => of([] as LivePipeline[]))),
      ),
    ).pipe(map((lists) => lists.flat()));
  }

  /**
   * Ein Projekt, dessen Abgleich scheitert, fehlt im Ergebnis und behaelt damit seine
   * Karten: der letzte Stand ist hoechstens veraltet, eine leere Liste dagegen falsch.
   */
  private reloadedLivePipelines(projectIds: string[]): Observable<Map<string, LivePipeline[]>> {
    return forkJoinOrEmpty(
      projectIds.map((projectId) =>
        this.projectLivePipelines(projectId).pipe(
          map((pipelines): [string, LivePipeline[]] => [projectId, pipelines]),
          catchError(() => of(null)),
        ),
      ),
    ).pipe(map((results) => new Map(results.filter((result) => result !== null))));
  }

  private projectLivePipelines(projectId: string): Observable<LivePipeline[]> {
    return this.feed.list(projectId).pipe(map((entries) => entries.map((entry) => ({ ...entry, projectId }))));
  }

  private replaceReloaded(reloaded: Map<string, LivePipeline[]>): void {
    this.pipelines.update((pipelines) =>
      newestFirst([
        ...pipelines.filter((pipeline) => !reloaded.has(pipeline.projectId)),
        ...[...reloaded.values()].flat().filter(isRunning),
      ]),
    );
  }

  /**
   * Ereignisse fremder Projekte fallen heraus: der Broker stellt nur abonnierte
   * Topics zu, aber die Sektion soll auch dann nichts zeigen, was sie beim Laden
   * nicht gezeigt haette.
   *
   * <p>Das Ende eines Durchlaufs nimmt seine Karte mit. Ein Durchlauf, der wieder zu
   * laufen anfaengt, kommt als neuer Eintrag zurueck — dafuer genuegt derselbe Zweig,
   * der auch einen erstmals gemeldeten aufnimmt.
   */
  private applyFeedEntry(projectId: string, entry: LivePipelineEntry): void {
    if (!this.watchedProjectIds().includes(projectId)) {
      return;
    }
    const updated: LivePipeline = { ...entry, projectId };
    const key = livePipelineKey(updated);
    this.pipelines.update((pipelines) => {
      const others = pipelines.filter((pipeline) => livePipelineKey(pipeline) !== key);
      return newestFirst(isRunning(updated) ? [...others, updated] : others);
    });
  }
}

function isRunning(pipeline: LivePipelineEntry): boolean {
  return pipeline.status === 'RUNNING';
}

/** Wie {@code forkJoin}, aber ohne Quelle mit einer leeren Liste statt nie. */
function forkJoinOrEmpty<T>(sources: Observable<T>[]): Observable<T[]> {
  return sources.length === 0 ? of([]) : forkJoin(sources);
}

function newestFirst(pipelines: LivePipeline[]): LivePipeline[] {
  return [...pipelines].sort((left, right) => Date.parse(right.startedAt) - Date.parse(left.startedAt));
}
